'use client';

import React, { useState } from 'react';
import { Filter, ChevronDown, X } from 'lucide-react';

type FilterChip = {
  id: string;
  label: string;
  value: string;
  group: 'status' | 'priority' | 'assignee';
};

const statusOptions = [
  { id: 'filter-status-all', label: 'All Statuses', value: 'all' },
  { id: 'filter-status-backlog', label: 'Backlog', value: 'backlog' },
  { id: 'filter-status-inprogress', label: 'In Progress', value: 'in-progress' },
  { id: 'filter-status-review', label: 'In Review', value: 'in-review' },
  { id: 'filter-status-done', label: 'Done', value: 'done' },
  { id: 'filter-status-blocked', label: 'Blocked', value: 'blocked' },
];

const priorityOptions = [
  { id: 'filter-pri-all', label: 'All Priorities', value: 'all' },
  { id: 'filter-pri-high', label: 'High', value: 'high' },
  { id: 'filter-pri-medium', label: 'Medium', value: 'medium' },
  { id: 'filter-pri-low', label: 'Low', value: 'low' },
];

const assigneeOptions = [
  { id: 'filter-assign-all', label: 'All Members', value: 'all' },
  { id: 'filter-assign-pn', label: 'Priya Nair', value: 'priya-nair' },
  { id: 'filter-assign-ka', label: 'Kwame Asante', value: 'kwame-asante' },
  { id: 'filter-assign-sd', label: 'Sofía Delgado', value: 'sofia-delgado' },
  { id: 'filter-assign-th', label: 'Tariq Hassan', value: 'tariq-hassan' },
  { id: 'filter-assign-ao', label: 'Amara Osei', value: 'amara-osei' },
  { id: 'filter-assign-lv', label: 'Lena Voigt', value: 'lena-voigt' },
  { id: 'filter-assign-rm', label: 'Raj Mehta', value: 'raj-mehta' },
  { id: 'filter-assign-ce', label: 'Chioma Eze', value: 'chioma-eze' },
];

export interface FilterState {
  status: string;
  priority: string;
  assignee: string;
}

interface DashboardFiltersProps {
  onFilterChange?: (filters: FilterState) => void;
}

export default function DashboardFilters({ onFilterChange }: DashboardFiltersProps) {
  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    priority: 'all',
    assignee: 'all',
  });
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const activeChips: FilterChip[] = [];
  if (filters.status !== 'all') {
    const opt = statusOptions.find((o) => o.value === filters.status);
    if (opt) activeChips.push({ id: `chip-status-${filters.status}`, label: opt.label, value: filters.status, group: 'status' });
  }
  if (filters.priority !== 'all') {
    const opt = priorityOptions.find((o) => o.value === filters.priority);
    if (opt) activeChips.push({ id: `chip-priority-${filters.priority}`, label: opt.label, value: filters.priority, group: 'priority' });
  }
  if (filters.assignee !== 'all') {
    const opt = assigneeOptions.find((o) => o.value === filters.assignee);
    if (opt) activeChips.push({ id: `chip-assignee-${filters.assignee}`, label: opt.label, value: filters.assignee, group: 'assignee' });
  }

  const updateFilter = (group: keyof FilterState, value: string) => {
    const next = { ...filters, [group]: value };
    setFilters(next);
    onFilterChange?.(next);
    setOpenDropdown(null);
  };

  const removeChip = (chip: FilterChip) => {
    updateFilter(chip.group, 'all');
  };

  const clearAll = () => {
    const reset = { status: 'all', priority: 'all', assignee: 'all' };
    setFilters(reset);
    onFilterChange?.(reset);
  };

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <div className="flex items-center gap-1.5 text-muted-foreground text-sm">
        <Filter size={14} />
        <span className="font-medium">Filters</span>
      </div>

      {/* Status dropdown */}
      <div className="relative">
        <button
          onClick={() => setOpenDropdown(openDropdown === 'status' ? null : 'status')}
          className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border text-sm font-medium transition-all duration-150 btn-press ${
            filters.status !== 'all' ?'border-primary bg-primary/10 text-primary' :'border-border bg-card text-foreground hover:border-primary/50'
          }`}
        >
          Status
          <ChevronDown size={13} className={`transition-transform ${openDropdown === 'status' ? 'rotate-180' : ''}`} />
        </button>
        {openDropdown === 'status' && (
          <div className="absolute left-0 top-full mt-1 z-20 bg-card border border-border rounded-xl shadow-card-md min-w-[160px] py-1 fade-in">
            {statusOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => updateFilter('status', opt.value)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                  filters.status === opt.value
                    ? 'bg-primary/10 text-primary font-semibold' :'text-foreground hover:bg-muted'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Priority dropdown */}
      <div className="relative">
        <button
          onClick={() => setOpenDropdown(openDropdown === 'priority' ? null : 'priority')}
          className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border text-sm font-medium transition-all duration-150 btn-press ${
            filters.priority !== 'all' ?'border-primary bg-primary/10 text-primary' :'border-border bg-card text-foreground hover:border-primary/50'
          }`}
        >
          Priority
          <ChevronDown size={13} className={`transition-transform ${openDropdown === 'priority' ? 'rotate-180' : ''}`} />
        </button>
        {openDropdown === 'priority' && (
          <div className="absolute left-0 top-full mt-1 z-20 bg-card border border-border rounded-xl shadow-card-md min-w-[160px] py-1 fade-in">
            {priorityOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => updateFilter('priority', opt.value)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                  filters.priority === opt.value
                    ? 'bg-primary/10 text-primary font-semibold' :'text-foreground hover:bg-muted'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Assignee dropdown */}
      <div className="relative">
        <button
          onClick={() => setOpenDropdown(openDropdown === 'assignee' ? null : 'assignee')}
          className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border text-sm font-medium transition-all duration-150 btn-press ${
            filters.assignee !== 'all' ?'border-primary bg-primary/10 text-primary' :'border-border bg-card text-foreground hover:border-primary/50'
          }`}
        >
          Assignee
          <ChevronDown size={13} className={`transition-transform ${openDropdown === 'assignee' ? 'rotate-180' : ''}`} />
        </button>
        {openDropdown === 'assignee' && (
          <div className="absolute left-0 top-full mt-1 z-20 bg-card border border-border rounded-xl shadow-card-md min-w-[180px] py-1 fade-in max-h-64 overflow-y-auto scrollbar-thin">
            {assigneeOptions.map((opt) => (
              <button
                key={opt.id}
                onClick={() => updateFilter('assignee', opt.value)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                  filters.assignee === opt.value
                    ? 'bg-primary/10 text-primary font-semibold' :'text-foreground hover:bg-muted'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Active chips */}
      {activeChips.map((chip) => (
        <span
          key={chip.id}
          className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg bg-primary/10 border border-primary/30 text-primary text-xs font-semibold"
        >
          {chip.label}
          <button
            onClick={() => removeChip(chip)}
            className="hover:text-red-500 transition-colors btn-press"
          >
            <X size={11} />
          </button>
        </span>
      ))}

      {activeChips.length > 0 && (
        <button
          onClick={clearAll}
          className="text-xs text-muted-foreground hover:text-red-500 font-medium transition-colors btn-press"
        >
          Clear all
        </button>
      )}

      {/* Close dropdowns on outside click — handled via blur/focus loss */}
      {openDropdown && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setOpenDropdown(null)}
        />
      )}
    </div>
  );
}